import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import { UpdateQuotationPayload } from 'components/ordering/quotation/api/requests/update-quotation.js'
import { CSETextarea, CSETextInput } from '@cloudsuite/mosaic-elements'
import { NotFoundError } from 'components/ordering/quotation/api/dtos/error.js'
import { consume } from '@lit/context'
import {
  activeQuotationContext,
  NotFetchedYet,
  updateQuotationContext
} from 'components/ordering/quotation/components/context/quotation-context.js'
import { isQuotationDTO } from 'components/ordering/quotation/components/is-quotation-dto.js'
import { formStyles, hostDisplayContents } from 'components/ordering/quotation/components/quotation.style.js'

@customElement('csc-quotation-request-form')
export class CSCQuotationRequestForm extends LitElement {
  static override styles: CSSResultGroup = [hostDisplayContents, formStyles]

  @consume({ context: activeQuotationContext, subscribe: true })
  private quotation!: QuotationDTO | NotFoundError | NotFetchedYet

  @consume({ context: updateQuotationContext })
  private updateQuotation!: (payload: UpdateQuotationPayload) => Promise<void>

  @state()
  private get isVisible(): boolean {
    return this.quotation instanceof QuotationDTO && this.quotation.status === 'open'
  }

  protected override updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties)

    if (!isQuotationDTO(this.quotation) || this.quotation.maxCharactersName === null) {
      return
    }

    const maxCharactersName = this.quotation.maxCharactersName
    const nameInput = this.renderRoot.querySelector('cse-text-input') as CSETextInput | null
    void nameInput?.updateComplete.then(() => {
      nameInput.input.maxLength = maxCharactersName
    })
  }

  private async _updateQuotation(event: Event, field: 'name' | 'note'): Promise<void> {
    if (!isQuotationDTO(this.quotation)) {
      return
    }

    const name = (field === 'name' ? (event.target as CSETextInput).value : this.quotation.name) || ''
    const note = (field === 'note' ? (event.target as CSETextarea).value : this.quotation.note) || ''

    const payload = {
      quotationId: this.quotation.id,
      status: this.quotation.status,
      name: name,
      note: note,
    }
    await this.updateQuotation(payload)
  }

  override render() {
    if (!isQuotationDTO(this.quotation) || !this.isVisible) {
      return nothing
    }

    return html`
      <div class="grid">
        <cse-fieldset class="form">
          <cse-form-field>
            <cse-label slot="label">
              ${t('csc_quotation_request_form_reference', {
                ns: 'csc-quotation-request-form',
                default: 'Reference'
              })}
            </cse-label>
            <cse-text-input
              value="${this.quotation.name}"
              @change="${(event: Event) => this._updateQuotation(event, 'name')}"
            ></cse-text-input>
          </cse-form-field>
  
          <cse-form-field>
            <cse-label slot="label">
              ${t('csc_quotation_request_form_comment', {
                ns: 'csc-quotation-request-form',
                default: 'Comment'
              })}
            </cse-label>
            <cse-textarea
              maxlength="100"
              value="${this.quotation.note}"
              @change="${(event: Event) => this._updateQuotation(event, 'note')}"
            ></cse-textarea>
          </cse-form-field>
        </cse-fieldset>
      </div>
    `
  }
}
